import {
    CheckBoxOutlineBlankOutlined,
    CheckBoxOutlined,
    FileDownloadOutlined,
} from "@mui/icons-material";
import { Checkbox, FormControlLabel, IconButton, Tooltip, Grid } from "@mui/material";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import AppConfirmDialog from "components/Shared/AppConfirmDialog";
import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import DateRangeToggle from "components/Shared/DateRangeToggle";
import { useAuth } from "hooks";
import { useLazyDownloadCocCollectionsQuery } from "store/services/cocCollectionApi";
import { useLazyGetDDLBranchListQuery } from "store/services/branchApi";
import { toast } from "react-toastify";
import moment from "moment";

const EXCEL_DATA_INITIAL_STATE = { dateRange: [], branch: null, isCollected: "" };

const exportCsvFile = ({ data, fileName }) => {
    const headers = [
        "SN",
        "COC Collection ID",
        "Branch",
        "Requested By",
        "Amount",
        "Method",
        "Requested Date",
        "Financed Date",
        "Clearance Status",
    ];

    const csvRows = [
        headers.join(","),
        ...data.map((row) =>
            [
                row.SN,
                `"${row["COC Collection ID"]}"`,
                `"${row.Branch}"`,
                `"${row["Requested By"]}"`,
                `"${row.Amount}"`,
                `"${row.Method}"`,
                `"${row["Requested Date"]}"`,
                `"${row["Financed Date"]}"`,
                `"${row["Clearance Status"]}"`,
            ].join(",")
        ),
    ];

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const ExportCocCollectionExcel = ({ isBusy = false }) => {
    const [showDialog, setShowDialog] = React.useState(false);
    const [showForm, setShowForm] = React.useState(false);
    const { viewEntity } = useAuth();
    const { control, handleSubmit, reset, formState: { errors } } = useForm({
        defaultValues: EXCEL_DATA_INITIAL_STATE,
    });

    const [fetchCocCollection] = useLazyDownloadCocCollectionsQuery();
    const [fetchBranches, { data: branches, isLoading: loadingBranches }] =
        useLazyGetDDLBranchListQuery();

    React.useEffect(() => {
        if (viewEntity?.entity?.account?._id && viewEntity?.entity?._id) {
            fetchBranches({
                account: viewEntity?.entity?.account?._id,
                entity: viewEntity?.entity?._id,
            });
        }
    }, [fetchBranches, viewEntity?.entity?._id, viewEntity?.entity?.account?._id]);

    const handleDownload = async (data) => {
        const { dateRange, branch, isCollected } = data;

        if (!dateRange || dateRange.length !== 2) {
            toast.error("Please select a valid date range.");
            return;
        }

        const fromDate = moment(dateRange[0]).format("MM-DD-YYYY");
        const toDate = moment(dateRange[1]).format("MM-DD-YYYY");

        const payload = {
            fromDate,
            toDate,
            ...(showForm && branch && { branch: branch._id }),
            ...(showForm && isCollected && { isCollected }),
        };

        try {
            const responseData = await fetchCocCollection(payload).unwrap();
            if (!responseData?.data || responseData.data.length === 0) {
                toast.info("No COC collection records found for the selected filters.");
                return;
            }

            const formattedData = responseData.data.map((row, index) => ({
                SN: index + 1,
                "COC Collection ID": row.cocCollectionId || "N/A",
                "Branch": row.branchName || "N/A",
                "Requested By": row.addedUser || "N/A",
                "Amount": row.collectionAmount != null ? row.collectionAmount.toFixed(2) : "N/A",
                "Method": row.collectionMethod || "N/A",
                "Requested Date": row.collectionDate
                    ? new Date(row.collectionDate).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                    })
                    : "N/A",
                "Financed Date": row.financedAt
                    ? new Date(row.financedAt).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                    })
                    : "N/A",
                "Clearance Status": row.isCollected ? "Cleared" : "Pending",
            }));

            exportCsvFile({
                data: formattedData,
                fileName: `COC_Collection_${new Date().toISOString().slice(0, 10)}.csv`,
            });
            toast.success("Data downloaded successfully!");
        } catch (error) {
            console.error("Export failed:", error);
            const errorMessage = error?.data?.message || error?.message || "Failed to export data. Please try again.";
            toast.error(errorMessage);
        } finally {
            handleReset();
        }
    };

    const handleReset = () => {
        setShowDialog(false);
        setShowForm(false);
        reset(EXCEL_DATA_INITIAL_STATE);
    };

    const branchOptions = React.useMemo(() => {
        const validBranches = Array.isArray(branches)
            ? branches.map((item) => ({
                label: item?.name || "Unknown",
                value: item?._id || "",
            }))
            : [];
        return [{ label: "All Branches", value: "" }, ...validBranches];
    }, [branches]);

    return (
        <>
            <Tooltip title="Download COC Collection CSV" arrow>
                <IconButton color="success" onClick={() => setShowDialog(true)}>
                    <FileDownloadOutlined />
                </IconButton>
            </Tooltip>

            <AppConfirmDialog
                open={showDialog}
                maxWidth="md"
                onClose={handleReset}
                onConfirm={handleSubmit(handleDownload)}
                title="Download COC Collections"
                dialogIcon={() => <FileDownloadOutlined color="success" />}
            >
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={showForm}
                            onChange={(e) => setShowForm(e.target.checked)}
                            icon={<CheckBoxOutlineBlankOutlined />}
                            checkedIcon={<CheckBoxOutlined />}
                        />
                    }
                    label="Filter by specific Branch and Status"
                />

                {showForm && (
                    <AppForm loading={isBusy || loadingBranches}>
                        <AppForm.Content>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6} md={12}>
                                    <Controller
                                        name="branch"
                                        control={control}
                                        rules={{
                                            validate: (value) =>
                                                !showForm || value || "Please select a branch or choose 'All Branches'.",
                                        }}
                                        render={({ field }) => (
                                            <BaseAutocomplete
                                                {...field}
                                                fullWidth
                                                label="Branch"
                                                options={branchOptions}
                                                getOptionLabel={(option) => option.label || "Select Branch"}
                                                isOptionEqualToValue={(option, value) => option.value === value?.value}
                                                loading={loadingBranches}
                                                loadingText="Loading branches..."
                                                onChange={(_, value) => field.onChange(value ? { _id: value.value, name: value.label } : null)}
                                                value={branchOptions.find((opt) => opt.value === field.value?.value) || null}
                                                error={!!errors.branch}
                                                helperText={errors.branch?.message}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={12}>
                                    <Controller
                                        name="isCollected"
                                        control={control}
                                        rules={{
                                            validate: (value) =>
                                                !showForm || value !== undefined || "Please select a clearance status.",
                                        }}
                                        render={({ field }) => (
                                            <BaseAutocomplete
                                                {...field}
                                                fullWidth
                                                label="Clearance Status"
                                                options={[
                                                    { label: "All Statuses", value: "" },
                                                    { label: "Cleared", value: "true" },
                                                    { label: "Pending", value: "false" },
                                                ]}
                                                getOptionLabel={(option) => option.label}
                                                isOptionEqualToValue={(option, value) => option.value === value?.value}
                                                onChange={(_, value) => field.onChange(value?.value || "")}
                                                value={
                                                    field.value === ""
                                                        ? { label: "All Statuses", value: "" }
                                                        : field.value === "true"
                                                            ? { label: "Cleared", value: "true" }
                                                            : { label: "Pending", value: "false" }
                                                }
                                                error={!!errors.isCollected}
                                                helperText={errors.isCollected?.message}
                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>
                        </AppForm.Content>
                    </AppForm>
                )}

                <Controller
                    name="dateRange"
                    control={control}
                    rules={{ required: "Date range is required" }}
                    render={({ field, fieldState: { error } }) => (
                        <DateRangeToggle
                            {...field}
                            label="Select Date Range"
                            error={!!error}
                            helperText={error?.message}
                            required
                        />
                    )}
                />
            </AppConfirmDialog>
        </>
    );
};

export default ExportCocCollectionExcel;